import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, 
  AlertTriangle,
  HelpCircle,
  Edit3
} from "lucide-react";
import { archetypeConfig } from "@/lib/archetypeConfig";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DecisionMatrixSkeleton } from "./DecisionMatrixSkeleton";
import { LogicGateValidator } from "./LogicGateValidator";
import { ConflictResolutionDialog, ConflictCell } from "./ConflictResolutionDialog";
import { cn } from "@/lib/utils";

interface Scenario {
  id: string;
  name: string;
  scenario_type: string;
  archetype?: string;
  strategic_goal?: string;
}

interface MatrixEntry {
  id?: string;
  session_id: string;
  option_id: string;
  criterion: string;
  score: string;
  meta?: Record<string, unknown>;
}

interface DynamicCriterion {
  key: string;
  label: string;
  description: string;
  phase1_reference: string;
}

interface DominantStrategy {
  type: "clear_winner" | "conditional" | "trade_off" | "hidden_winner";
  option_name?: string;
  explanation: string;
  key_insights: string[];
}

// New: Cell state simplified - auto-confirmed by default
interface CellState {
  status: "auto_confirmed" | "user_modified" | "conflict_pending" | "conflict_resolved";
  score: string;
  proposedReason?: string;
  tradeOffReason?: string; // Only set if conflict was resolved with trade-off
}

// New: Phase 1 conflict data from backend
interface Phase1Conflict {
  optionId: string;
  criterion: string;
  phase1Reference: {
    type: 'tension' | 'assumption' | 'risk' | 'success_criteria';
    text: string;
    source: string;
  };
  conflictReason: string;
  isNonNegotiable?: boolean; // BLOCKING - requires explicit acknowledgment
}

interface DecisionMatrixProps {
  sessionId: string;
  onMatrixComplete: (dominantStrategy: DominantStrategy | null) => void;
}

// Fallback criteria if dynamic generation fails
const FALLBACK_CRITERIA: DynamicCriterion[] = [
  { key: "value_potential", label: "Wertpotenzial", description: "Erwarteter ROI und Business Impact", phase1_reference: "hypothesis" },
  { key: "risk_profile", label: "Risikoprofil", description: "Unsicherheiten und Downside (HOCH = niedriges Risiko)", phase1_reference: "tension" },
  { key: "execution_fit", label: "Umsetzbarkeit", description: "Ressourcen und Implementierungskomplexität", phase1_reference: "success_criteria" },
  { key: "strategic_alignment", label: "Strategiefit", description: "Passung zu langfristigen Zielen", phase1_reference: "hypothesis" }
];

export const DecisionMatrix = ({ sessionId, onMatrixComplete }: DecisionMatrixProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [matrixEntries, setMatrixEntries] = useState<MatrixEntry[]>([]);
  const [dynamicCriteria, setDynamicCriteria] = useState<DynamicCriterion[]>(FALLBACK_CRITERIA);
  const [proposedReasons, setProposedReasons] = useState<Record<string, Record<string, string>>>({});
  
  // Cell states: now auto_confirmed by default
  const [cellStates, setCellStates] = useState<Record<string, CellState>>({});
  
  // Phase 1 conflicts from backend
  const [phase1Conflicts, setPhase1Conflicts] = useState<Phase1Conflict[]>([]);
  
  // Blocking state from logic gates (archetype violations)
  const [blockedOptions, setBlockedOptions] = useState<Set<string>>(new Set());
  
  // Conflict resolution dialog
  const [activeConflict, setActiveConflict] = useState<ConflictCell | null>(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [sessionId]);

  // NEW: Quality-based metrics instead of confirmation counting
  const qualityMetrics = useMemo(() => {
    const nonBaselineScenarios = scenarios.filter(s => s.scenario_type !== 'baseline');
    
    // Count user modifications (active disagreements)
    let userModifications = 0;
    
    // Count resolved conflicts (conscious trade-offs)
    let conflictsResolved = 0;
    let conflictsPending = 0;
    
    // Count trade-offs with reasons
    let tradeOffsAccepted = 0;
    
    nonBaselineScenarios.forEach(scenario => {
      dynamicCriteria.forEach(criterion => {
        const key = `${scenario.id}-${criterion.key}`;
        const state = cellStates[key];
        
        if (state?.status === "user_modified") userModifications++;
        if (state?.status === "conflict_resolved") {
          conflictsResolved++;
          if (state.tradeOffReason) tradeOffsAccepted++;
        }
        if (state?.status === "conflict_pending") conflictsPending++;
      });
    });
    
    // Check if all Phase 1 conflicts are resolved
    const allConflictsResolved = conflictsPending === 0;
    const hasNoBlockers = blockedOptions.size === 0;
    
    return {
      userModifications,
      conflictsResolved,
      conflictsPending,
      tradeOffsAccepted,
      totalConflicts: conflictsResolved + conflictsPending,
      isComplete: allConflictsResolved && hasNoBlockers,
      hasNoBlockers
    };
  }, [scenarios, dynamicCriteria, cellStates, blockedOptions, phase1Conflicts]);

  // Trigger recommendation when all conflicts are resolved
  useEffect(() => {
    if (qualityMetrics.isComplete && scenarios.length > 0) {
      generateRecommendation();
    }
  }, [qualityMetrics.isComplete, scenarios.length]);

  // Calculate column completion status for validators
  const columnCompletionStatus = useMemo(() => {
    const status: Record<string, { isComplete: boolean; scores: Record<string, string> }> = {};
    
    scenarios.forEach(scenario => {
      const scores: Record<string, string> = {};
      let hasAllScores = true;
      
      dynamicCriteria.forEach(criterion => {
        const key = `${scenario.id}-${criterion.key}`;
        const state = cellStates[key];
        if (state?.score) {
          scores[criterion.key] = state.score;
        } else {
          hasAllScores = false;
        }
      });
      
      status[scenario.id] = {
        isComplete: hasAllScores,
        scores
      };
    });
    
    return status;
  }, [scenarios, dynamicCriteria, cellStates]);

  const loadData = async () => {
    try {
      // Load scenarios
      const { data: scenariosData, error: scenariosError } = await supabase
        .from("scenarios")
        .select("*")
        .eq("session_id", sessionId)
        .order("order_index");

      if (scenariosError) throw scenariosError;
      setScenarios(scenariosData || []);

      // Load session meta for dynamic criteria and Phase 1 conflicts
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();
      
      const meta = session?.meta as Record<string, unknown> | null;
      if (meta?.phase3_criteria) {
        setDynamicCriteria(meta.phase3_criteria as DynamicCriterion[]);
      }
      if (meta?.phase3_proposed_reasons) {
        setProposedReasons(meta.phase3_proposed_reasons as Record<string, Record<string, string>>);
      }
      // Load Phase 1 conflicts
      if (meta?.phase1_conflicts) {
        setPhase1Conflicts(meta.phase1_conflicts as Phase1Conflict[]);
      }

      // Load matrix entries
      const { data: entriesData, error: entriesError } = await supabase
        .from("decision_matrix_entries")
        .select("*")
        .eq("session_id", sessionId);

      if (entriesError) throw entriesError;

      if (entriesData && entriesData.length > 0) {
        setMatrixEntries(entriesData);
        
        // NEW: Initialize cell states as AUTO_CONFIRMED (not proposed)
        // Only cells with Phase 1 conflicts are marked as conflict_pending
        const conflicts = (meta?.phase1_conflicts || []) as Phase1Conflict[];
        const conflictKeys = new Set(conflicts.map(c => `${c.optionId}-${c.criterion}`));
        
        const states: Record<string, CellState> = {};
        entriesData.forEach(e => {
          const key = `${e.option_id}-${e.criterion}`;
          const reason = (meta?.phase3_proposed_reasons as Record<string, Record<string, string>>)?.[e.option_id]?.[e.criterion];
          
          // Trade-off reasons are stored in session meta, not per-entry
          const tradeOffKey = `${e.option_id}-${e.criterion}`;
          const tradeOffs = (meta?.trade_off_reasons || {}) as Record<string, string>;
          const hasTradeOffReason = tradeOffs[tradeOffKey];
          
          states[key] = {
            // If has conflict and no trade-off reason yet -> pending
            // If has conflict and trade-off reason -> resolved  
            // Otherwise -> auto_confirmed
            status: conflictKeys.has(key) 
              ? (hasTradeOffReason ? "conflict_resolved" : "conflict_pending")
              : "auto_confirmed",
            score: e.score,
            proposedReason: reason,
            tradeOffReason: hasTradeOffReason as string | undefined
          };
        });
        setCellStates(states);
      } else if (scenariosData && scenariosData.length > 0) {
        // Generate initial matrix entries
        await generateMatrix();
      }
    } catch (error: unknown) {
      console.error("Error loading data:", error);
      toast({
        title: "Fehler",
        description: "Die Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateMatrix = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("phase2-compare", {
        body: { session_id: sessionId },
      });

      if (error) throw error;

      if (data?.matrix_entries) {
        setMatrixEntries(data.matrix_entries);
        
        // Store dynamic criteria
        if (data.dynamic_criteria) {
          setDynamicCriteria(data.dynamic_criteria);
        }
        if (data.proposed_reasons) {
          setProposedReasons(data.proposed_reasons);
        }
        // Store Phase 1 conflicts
        if (data.phase1_conflicts) {
          setPhase1Conflicts(data.phase1_conflicts);
        }
        
        // NEW: Initialize as AUTO_CONFIRMED, mark conflicts as pending
        const conflictKeys = new Set((data.phase1_conflicts || []).map((c: Phase1Conflict) => `${c.optionId}-${c.criterion}`));
        
        const states: Record<string, CellState> = {};
        data.matrix_entries.forEach((e: MatrixEntry) => {
          const key = `${e.option_id}-${e.criterion}`;
          states[key] = {
            status: conflictKeys.has(key) ? "conflict_pending" : "auto_confirmed",
            score: e.score,
            proposedReason: data.proposed_reasons?.[e.option_id]?.[e.criterion]
          };
        });
        setCellStates(states);
      }
    } catch (error: unknown) {
      console.error("Error generating matrix:", error);
      toast({
        title: "Fehler",
        description: "Die Matrix konnte nicht generiert werden.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateRecommendation = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("phase2-recommend", {
        body: { session_id: sessionId },
      });

      if (error) throw error;

      if (data?.recommendation?.reasoning?.dominant_strategy) {
        onMatrixComplete(data.recommendation.reasoning.dominant_strategy);
      } else {
        onMatrixComplete(null);
      }
    } catch (error: unknown) {
      console.error("Error generating recommendation:", error);
      onMatrixComplete(null);
    }
  };

  const updateScore = async (optionId: string, criterion: string, newScore: string) => {
    const key = `${optionId}-${criterion}`;
    const prevState = cellStates[key];
    
    try {
      const existingEntry = matrixEntries.find(
        (e) => e.option_id === optionId && e.criterion === criterion
      );

      if (existingEntry?.id) {
        const { error } = await supabase
          .from("decision_matrix_entries")
          .update({ score: newScore })
          .eq("id", existingEntry.id);

        if (error) throw error;

        setMatrixEntries(
          matrixEntries.map((e) =>
            e.id === existingEntry.id ? { ...e, score: newScore } : e
          )
        );
      } else {
        const newEntry: MatrixEntry = {
          session_id: sessionId,
          option_id: optionId,
          criterion,
          score: newScore,
        };

        const { data, error } = await supabase
          .from("decision_matrix_entries")
          .insert([newEntry])
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setMatrixEntries([...matrixEntries, data]);
        }
      }

      // Mark as user_modified (user actively changed this value)
      // If it was a conflict, changing the value resolves it
      setCellStates(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: prevState?.status === "conflict_pending" ? "conflict_resolved" : "user_modified",
          score: newScore
        }
      }));
    } catch (error: unknown) {
      console.error("Error updating score:", error);
      toast({
        title: "Fehler",
        description: "Der Score konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  // Handle conflict resolution with trade-off reason
  const handleResolveConflict = async (conflict: ConflictCell, tradeOffReason: string) => {
    const key = `${conflict.optionId}-${conflict.criterion}`;
    
    try {
      // Store trade-off reason in session meta (since decision_matrix_entries doesn't have meta column)
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();
      
      const currentMeta = (session?.meta || {}) as Record<string, unknown>;
      const tradeOffs = (currentMeta.trade_off_reasons || {}) as Record<string, string>;
      
      const { error } = await supabase
        .from("decision_sessions")
        .update({
          meta: {
            ...currentMeta,
            trade_off_reasons: {
              ...tradeOffs,
              [key]: tradeOffReason
            }
          }
        })
        .eq("id", sessionId);

      if (error) throw error;

      // Mark cell as conflict_resolved with trade-off reason
      setCellStates(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          status: "conflict_resolved",
          tradeOffReason
        }
      }));

      setShowConflictDialog(false);
      setActiveConflict(null);

      toast({
        title: "Trade-off akzeptiert",
        description: "Deine Begründung wurde gespeichert.",
      });
    } catch (error: unknown) {
      console.error("Error resolving conflict:", error);
      toast({
        title: "Fehler",
        description: "Der Konflikt konnte nicht aufgelöst werden.",
        variant: "destructive",
      });
    }
  };

  const handleBlockingGate = (optionId: string, isBlocked: boolean) => {
    setBlockedOptions(prev => {
      const newSet = new Set(prev);
      if (isBlocked) {
        newSet.add(optionId);
      } else {
        newSet.delete(optionId);
      }
      return newSet;
    });
  };

  const getScore = (optionId: string, criterion: string): string => {
    const key = `${optionId}-${criterion}`;
    return cellStates[key]?.score || "";
  };

  const getCellState = (optionId: string, criterion: string): CellState | undefined => {
    return cellStates[`${optionId}-${criterion}`];
  };

  const getScoreColor = (score: string) => {
    switch (score) {
      case "high":
        return "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800";
      case "medium":
        return "text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 border-amber-200 dark:border-amber-800";
      case "low":
        return "text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-400 border-red-200 dark:border-red-800";
      default:
        return "text-muted-foreground bg-muted border-border";
    }
  };

  // Check if a cell has a Phase 1 conflict
  const getCellConflict = (optionId: string, criterion: string): Phase1Conflict | undefined => {
    return phase1Conflicts.find(c => c.optionId === optionId && c.criterion === criterion);
  };

  // Open conflict dialog for a specific cell
  const openConflictDialog = (optionId: string, criterion: string) => {
    const conflict = getCellConflict(optionId, criterion);
    const scenario = scenarios.find(s => s.id === optionId);
    const criterionData = dynamicCriteria.find(c => c.key === criterion);
    const state = getCellState(optionId, criterion);
    
    if (conflict && scenario && criterionData) {
      setActiveConflict({
        optionId,
        optionName: scenario.name,
        criterion,
        criterionLabel: criterionData.label,
        phase1Reference: conflict.phase1Reference,
        currentScore: state?.score || 'medium',
        conflictReason: conflict.conflictReason
      });
      setShowConflictDialog(true);
    }
  };

  const nonBaselineScenarios = scenarios.filter(s => s.scenario_type !== 'baseline');
  const baselineScenario = scenarios.find(s => s.scenario_type === 'baseline');
  
  // Only show baseline if it has meaningful data (scores)
  const baselineHasData = baselineScenario ? dynamicCriteria.some(c => 
    getScore(baselineScenario.id, c.key) !== ''
  ) : false;
  
  // Scenarios to display: non-baseline + baseline only if it has data
  const displayScenarios = baselineHasData 
    ? scenarios 
    : nonBaselineScenarios;

  if (isLoading) {
    return <DecisionMatrixSkeleton />;
  }

  if (isGenerating) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-sm text-primary font-medium">
            AI analysiert Phase 1 und erstellt kontextspezifische Bewertungskriterien...
          </p>
        </div>
        <DecisionMatrixSkeleton />
      </div>
    );
  }

  if (scenarios.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          Keine Optionen gefunden. Bitte kehre zu Phase 2 zurück.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Conflict indicator - only show if there are pending conflicts */}
      {qualityMetrics.conflictsPending > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {qualityMetrics.conflictsPending} {qualityMetrics.conflictsPending === 1 ? 'Widerspruch' : 'Widersprüche'} zu Phase 1 – klicke auf die markierten Zellen.
          </p>
        </div>
      )}

      <ScrollArea className="w-full">
        <div className="border rounded-lg overflow-hidden min-w-[700px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">
                  <div className="flex items-center gap-1">
                    Kriterium
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[280px]">
                          <p className="text-xs">
                            Diese Kriterien wurden <strong>aus Phase 1</strong> abgeleitet, 
                            basierend auf deinem Spannungsfeld und deiner Kernhypothese.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableHead>
                {displayScenarios.map((scenario) => {
                  const config = scenario.archetype ? archetypeConfig[scenario.archetype] : null;
                  const Icon = config?.icon;
                  const isBaseline = scenario.scenario_type === 'baseline';
                  const isBlocked = blockedOptions.has(scenario.id);
                  const isWarningArchetype = config?.isWarning;
                  
                  return (
                    <TableHead 
                      key={scenario.id} 
                      className={cn(
                        "text-center min-w-[160px]", 
                        isBaseline && "opacity-60",
                        isBlocked && "bg-destructive/5",
                        isWarningArchetype && "bg-destructive/5"
                      )}
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        {isBaseline ? (
                          <Badge variant="outline" className="text-xs px-2 py-0.5 text-muted-foreground border-border">
                            Baseline
                          </Badge>
                        ) : config && Icon ? (
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs px-2 py-0.5 gap-1", config.color)}
                          >
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </Badge>
                        ) : null}
                        <span className={cn(
                          "font-semibold truncate max-w-[140px]",
                          isBaseline && "text-muted-foreground",
                          isWarningArchetype && "text-destructive"
                        )}>
                          {scenario.name}
                        </span>
                        {/* Warning message for scaling/deferral archetype */}
                        {isWarningArchetype && config?.warningMessage && (
                          <span className="text-[9px] text-destructive leading-tight max-w-[140px] text-center">
                            {config.warningMessage}
                          </span>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dynamicCriteria.map((criterion, index) => {
                // First criterion is the K.O. criterion (Non-Negotiable from Phase 1)
                const isKOCriterion = index === 0;
                
                return (
                <TableRow key={criterion.key} className={cn(isKOCriterion && "bg-primary/5")}>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-sm">{criterion.label}</span>
                              {isKOCriterion && (
                                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/30 font-semibold">
                                  K.O.
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[160px]">
                              {criterion.description}
                            </div>
                            {isKOCriterion && (
                              <div className="text-[10px] text-primary mt-0.5">
                                Muss HOCH sein für Commit
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[280px]">
                          <p className="text-xs mb-1">{criterion.description}</p>
                          <p className="text-xs text-muted-foreground">
                            Abgeleitet aus: {criterion.phase1_reference}
                          </p>
                          {isKOCriterion && (
                            <p className="text-xs text-primary mt-2 font-medium">
                              K.O.-Kriterium: Optionen mit &quot;Niedrig&quot; oder &quot;Mittel&quot; hier sind nicht commitfähig.
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  {displayScenarios.map((scenario) => {
                    const score = getScore(scenario.id, criterion.key);
                    const state = getCellState(scenario.id, criterion.key);
                    const conflict = getCellConflict(scenario.id, criterion.key);
                    const isConflictPending = state?.status === "conflict_pending";
                    const isConflictResolved = state?.status === "conflict_resolved";
                    const isUserModified = state?.status === "user_modified";
                    
                    return (
                      <TableCell key={scenario.id} className="text-center">
                        <div className="relative inline-block">
                          <Select
                            value={score}
                            onValueChange={(value) =>
                              updateScore(scenario.id, criterion.key, value)
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                "w-[110px] mx-auto transition-all",
                                score && getScoreColor(score),
                                isConflictPending && "ring-2 ring-amber-500/50 ring-offset-1",
                                isConflictResolved && "ring-2 ring-emerald-500/30 ring-offset-1",
                                isUserModified && "ring-2 ring-primary/30 ring-offset-1"
                              )}
                            >
                              <SelectValue placeholder="–" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">Hoch</SelectItem>
                              <SelectItem value="medium">Mittel</SelectItem>
                              <SelectItem value="low">Niedrig</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Conflict Badge - requires action */}
                          {isConflictPending && conflict && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge 
                                    variant="secondary" 
                                    className="absolute -top-2 -right-2 text-[9px] px-1.5 py-0 h-4 bg-amber-500/15 text-amber-600 border-amber-500/30 font-semibold cursor-pointer hover:bg-amber-500/25"
                                    onClick={() => openConflictDialog(scenario.id, criterion.key)}
                                  >
                                    <AlertTriangle className="h-2.5 w-2.5" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px]">
                                  <p className="text-xs font-medium mb-1 text-amber-600">Widerspruch zu Phase 1</p>
                                  <p className="text-xs text-muted-foreground mb-2">
                                    {conflict.conflictReason}
                                  </p>
                                  <p className="text-xs text-amber-600">
                                    Klicke um aufzulösen
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          
                          {/* User modified badge */}
                          {isUserModified && (
                            <Badge 
                              variant="secondary" 
                              className="absolute -top-2 -right-2 text-[9px] px-1 py-0 h-4 bg-primary/15 text-primary border-primary/30"
                            >
                              <Edit3 className="h-2.5 w-2.5" />
                            </Badge>
                          )}
                          
                          {/* Conflict resolved badge */}
                          {isConflictResolved && state?.tradeOffReason && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge 
                                    variant="secondary" 
                                    className="absolute -top-2 -right-2 text-[9px] px-1 py-0 h-4 bg-emerald-500/15 text-emerald-600 border-emerald-500/30"
                                  >
                                    ✓
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px]">
                                  <p className="text-xs font-medium mb-1 text-emerald-600">Trade-off akzeptiert</p>
                                  <p className="text-xs text-muted-foreground italic">
                                    "{state.tradeOffReason}"
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              )})}
              
              {/* WEIGHTED TOTALS ROW */}
              <TableRow className="border-t-2 bg-muted/30">
                <TableCell className="font-semibold">
                  <div>
                    <span className="text-sm">Gesamt</span>
                    <span className="text-xs text-muted-foreground block">
                      (gewichtet: je {Math.round(100 / dynamicCriteria.length)}%)
                    </span>
                  </div>
                </TableCell>
                {displayScenarios.map((scenario) => {
                  // Calculate weighted score for this option
                  const weight = 1 / dynamicCriteria.length;
                  let totalScore = 0;
                  let completedCriteria = 0;
                  
                  dynamicCriteria.forEach(criterion => {
                    const score = getScore(scenario.id, criterion.key);
                    if (score) {
                      const numericScore = score === 'high' ? 3 : score === 'medium' ? 2 : 1;
                      totalScore += numericScore * weight;
                      completedCriteria++;
                    }
                  });
                  
                  const displayScore = completedCriteria > 0 
                    ? (totalScore / (completedCriteria * weight) * weight * dynamicCriteria.length).toFixed(1)
                    : '–';
                  
                  // Find the highest score among displayScenarios to mark leader
                  const allScores = displayScenarios.map(s => {
                    let total = 0;
                    let count = 0;
                    dynamicCriteria.forEach(c => {
                      const sc = getScore(s.id, c.key);
                      if (sc) {
                        total += sc === 'high' ? 3 : sc === 'medium' ? 2 : 1;
                        count++;
                      }
                    });
                    return count > 0 ? total : 0;
                  });
                  
                  const myScore = (() => {
                    let total = 0;
                    let count = 0;
                    dynamicCriteria.forEach(c => {
                      const sc = getScore(scenario.id, c.key);
                      if (sc) {
                        total += sc === 'high' ? 3 : sc === 'medium' ? 2 : 1;
                        count++;
                      }
                    });
                    return count > 0 ? total : 0;
                  })();
                  
                  const maxScore = Math.max(...allScores);
                  const isLeader = myScore > 0 && myScore === maxScore && allScores.filter(s => s === maxScore).length === 1;
                  const isBlocked = blockedOptions.has(scenario.id);
                  
                  return (
                    <TableCell key={scenario.id} className="text-center">
                      <div className={cn(
                        "inline-flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg",
                        isLeader && !isBlocked && "bg-primary/10 border border-primary/30"
                      )}>
                        <span className={cn(
                          "text-lg font-bold",
                          isLeader && !isBlocked ? "text-primary" : "text-foreground"
                        )}>
                          {displayScore}
                        </span>
                        {isLeader && !isBlocked && (
                          <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">
                            Führt
                          </span>
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
              
              {/* Logic Gate Validation Row */}
              <TableRow className="border-t">
                <TableCell className="text-xs text-muted-foreground">
                  Validierung
                </TableCell>
                {displayScenarios.map((scenario) => {
                  const status = columnCompletionStatus[scenario.id];
                  
                  return (
                    <TableCell key={scenario.id} className="text-center align-top">
                      <LogicGateValidator
                        sessionId={sessionId}
                        optionId={scenario.id}
                        optionName={scenario.name}
                        archetype={scenario.archetype || "unknown"}
                        scores={status?.scores || {}}
                        isComplete={status?.isComplete || false}
                        onBlockingGate={(blocked) => handleBlockingGate(scenario.id, blocked)}
                        onResolveGate={(criterion) => {
                          toast({
                            title: "Wert anpassen",
                            description: `Bitte passe den Wert für "${criterion}" an.`,
                          });
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* NEW: Quality-based Progress Indicator */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
          {qualityMetrics.userModifications > 0 && (
            <div className="flex items-center gap-1.5">
              <Edit3 className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">
                <strong>{qualityMetrics.userModifications}</strong> Anpassungen
              </span>
            </div>
          )}
          
          {qualityMetrics.totalConflicts > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle className={cn(
                "h-3.5 w-3.5",
                qualityMetrics.conflictsPending > 0 ? "text-amber-500" : "text-emerald-500"
              )} />
              <span className="text-muted-foreground">
                <strong>{qualityMetrics.conflictsResolved}</strong> von <strong>{qualityMetrics.totalConflicts}</strong> Konflikten gelöst
              </span>
            </div>
          )}
          
          {qualityMetrics.tradeOffsAccepted > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-emerald-500">✓</span>
              <span className="text-muted-foreground">
                <strong>{qualityMetrics.tradeOffsAccepted}</strong> Trade-offs akzeptiert
              </span>
            </div>
          )}
        </div>
        
        {/* Status Message */}
        <p className="text-sm text-muted-foreground">
          {qualityMetrics.isComplete
            ? "✓ Bereit für Report – alle Konflikte aufgelöst" 
            : qualityMetrics.conflictsPending > 0
              ? `${qualityMetrics.conflictsPending} Phase-1-Widersprüche müssen noch aufgelöst werden`
              : blockedOptions.size > 0
                ? `${blockedOptions.size} Option(en) haben Archetype-Konflikte`
                : "Passe Werte an, bei denen du anderer Meinung bist"
          }
        </p>
        
        {baselineScenario && !qualityMetrics.isComplete && (
          <p className="text-xs text-muted-foreground">
            Baseline ist optional – fokussiere dich auf die strategischen Optionen
          </p>
        )}
      </div>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        conflict={activeConflict}
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        onResolve={handleResolveConflict}
        onChangeValue={(conflict) => {
          toast({
            title: "Wert ändern",
            description: `Wähle einen anderen Wert für "${conflict.criterionLabel}"`,
          });
        }}
      />
    </div>
  );
};
